/**
 * ProviderConfigDrawer — 框架通道 App 配置的新增 / 编辑抽屉（设计稿 PayerMax A2，`Drawer` 720；AIRWALLEX 沿用同款）。
 *
 * `PUT …/:channel/configs/:appId`（A8 §10.1）：
 *   - 凭据字段按能力声明 `credential_fields[]` 渲染：`required` 决定星号与必填，`secret` 决定遮蔽输入；
 *     只写、永不回填，不显示掩码 / 长度 / 尾号。编辑态「更换凭据」关闭 = 沿用当前凭据、不提交凭据组。
 *   - 商户号只录一次：凭据里与 `merchant_account_id` 逐字相等的字段（PAYERMAX `merchant_no`）由商户号同步。
 *   - 原生 App 只填回跳 host，path 固定；Web App 不适用、不提交该字段。
 *   - 改商户号或 mode = 换商户账户组，沿用的凭据会被拒，「更换凭据」锁定开启。
 *   - 已启用且已连接的配置保存前确认：保存使其回到待验证，C 端立即停止下发。
 *
 * 幂等：一次表单意图一枚 UUID v7 键；任一字段改动即换新键，内容不变的重试复用同一键（服务端回放）。
 * 秘密：成功保存后抽屉卸载即丢弃；`CONFIG_VERSION_CHANGED` 时清空密文、保留非密文字段。
 */

import type {
  PaymentProviderCapabilityResponse,
  PaymentProviderChannel,
  PaymentProviderConfigResponse,
  PaymentProviderMode,
  UpsertPaymentProviderConfigRequest,
} from '@novel-admin/shared';
import { PAYMENT_PROVIDER_RETURN_HOST_NOT_REGISTERED } from '@novel-admin/shared';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { AdminApiError } from '~/api';
import type { AppRow } from '~/data/queries';
import { paymentProviderConfigApi } from '~/hooks/use-payment-provider-config';
import { detailsReasonIs } from '~/lib/details-reason';
import { Icons } from '~/lib/icons';
import { newIdempotencyKeyV7 } from '~/lib/idempotency-key';
import { AppIcon } from '~/ui/AppIcon';
import { ConfirmDialog, DialogBody, Drawer } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { HostOnlyUrlInput } from '~/ui/HostOnlyUrlInput';
import { PasswordInput } from '~/ui/PasswordInput';
import { Select } from '~/ui/Select';
import { Switch } from '~/ui/Switch';
import { toast } from '~/ui/Toast';
import { ProviderNoticeBlock } from './ProviderNoticeBlock';
import {
  NATIVE_RETURN_PATH,
  groupHasMaterial,
  isNativePlatform,
  isValidReturnHost,
  merchantGroupKey,
  nativeReturnHostOf,
  nativeReturnUrlFor,
  normalizeKeyMaterial,
  normalizeReturnHost,
  providerPlatformOf,
} from './payment-provider-config-view';
import {
  type ProviderNotice,
  describeProviderError,
  noticeToastText,
  noticeToastTone,
} from './payment-provider-error-notice';
import { providerChannelCopy } from './provider-channel-copy';

const MERCHANT_MAX = 128;
const LABEL_WIDTH = 180;

export type ConfigDrawerTarget =
  | { mode: 'new'; appId?: string }
  | { mode: 'edit'; appId: string; replaceCredential?: boolean };

type FieldErrors = Partial<Record<string, string>>;

export function ProviderConfigDrawer({
  channel,
  capability,
  target,
  existing,
  configs,
  apps,
  candidates,
  knownAllowedHosts,
  onAllowedHosts,
  onClose,
  onSaved,
  onRefresh,
  onOpenRotate,
}: {
  channel: PaymentProviderChannel;
  capability: PaymentProviderCapabilityResponse;
  target: ConfigDrawerTarget;
  /** 编辑态：当前（最新读到的）配置 */
  existing: PaymentProviderConfigResponse | undefined;
  configs: PaymentProviderConfigResponse[];
  apps: AppRow[];
  /** 新增态可选的 App（平台适用且未配置） */
  candidates: AppRow[];
  knownAllowedHosts: Record<string, string[]>;
  onAllowedHosts: (appId: string, hosts: string[]) => void;
  onClose: () => void;
  onSaved: () => void;
  onRefresh: () => void;
  onOpenRotate: (groupKey: string) => void;
}) {
  const isEdit = target.mode === 'edit';
  // 凭据字段的展示约定（只管「怎么录」，必填与是否秘密一律取能力声明），见 `provider-channel-copy.ts`
  const copy = providerChannelCopy(channel);
  const presentation = {
    merchantField: copy.merchantCredentialField,
    verificationField: copy.verificationCredentialField,
    multilineFields: copy.multilineCredentialFields,
  };
  const deploymentMode = capability.deployment_mode;

  const [appId, setAppId] = useState<string>(
    target.mode === 'edit' ? target.appId : (target.appId ?? candidates[0]?.id ?? ''),
  );
  const [merchant, setMerchant] = useState(existing?.merchant_account_id ?? '');
  const [mode, setMode] = useState<PaymentProviderMode | null>(
    existing?.mode ?? deploymentMode ?? null,
  );
  const [replaceCredential, setReplaceCredential] = useState(
    target.mode === 'new' || target.replaceCredential === true,
  );
  const [credential, setCredential] = useState<Record<string, string>>({});
  const [host, setHost] = useState(nativeReturnHostOf(existing?.native_return_base_url));
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKeyV7());
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [hostRejection, setHostRejection] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<ProviderNotice | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [versionConflict, setVersionConflict] = useState(false);
  const [materialMismatch, setMaterialMismatch] = useState(false);

  const app = apps.find((a) => a.id === appId);
  const platform = providerPlatformOf(app);
  const native = isNativePlatform(platform);
  const appName = app?.name ?? appId;
  const trimmedMerchant = merchant.trim();

  // 改商户号或 mode = 换组：沿用的凭据与新组不匹配，必须重交整组凭据
  const groupChanged =
    isEdit &&
    !!existing &&
    (trimmedMerchant !== existing.merchant_account_id || mode !== existing.mode);
  const submitCredential = !isEdit || replaceCredential || groupChanged;

  const targetGroupKey = mode && trimmedMerchant ? merchantGroupKey(trimmedMerchant, mode) : null;
  const targetGroupMembers = useMemo(
    () =>
      targetGroupKey
        ? configs.filter(
            (c) =>
              merchantGroupKey(c.merchant_account_id, c.mode) === targetGroupKey &&
              c.app_id !== appId,
          )
        : [],
    [configs, targetGroupKey, appId],
  );
  const targetGroupEndpoints = targetGroupMembers[0]?.webhook_endpoints ?? [];
  const targetGroupHasMaterial = groupHasMaterial(
    targetGroupEndpoints.length > 0
      ? targetGroupEndpoints
      : groupChanged || !existing
        ? []
        : existing.webhook_endpoints,
  );

  /** 任一字段改动：换新幂等键并清掉该字段的服务端报错 */
  const touch = (field?: string) => {
    setIdempotencyKey(newIdempotencyKeyV7());
    setNotice(null);
    if (field) setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    setVersionConflict(false);
    if (
      (presentation.verificationField !== null && field === presentation.verificationField) ||
      field === 'merchant' ||
      field === 'mode' ||
      field === 'credential'
    ) {
      setMaterialMismatch(false);
    }
  };

  const credentialValue = (name: string) =>
    presentation.merchantField !== null && name === presentation.merchantField
      ? trimmedMerchant
      : (credential[name] ?? '');

  const credentialMissing = capability.credential_fields.some((f) => {
    if (!f.required) return false;
    const value = credentialValue(f.name);
    return presentation.multilineFields.includes(f.name)
      ? normalizeKeyMaterial(value) === ''
      : value.trim() === '';
  });
  const normalizedHost = normalizeReturnHost(host);
  const hostInvalid = native && !isValidReturnHost(normalizedHost);
  const merchantInvalid = trimmedMerchant === '' || trimmedMerchant.length > MERCHANT_MAX;
  const blockedReason =
    deploymentMode === null
      ? '本环境未声明 mode，保存必然被拒'
      : !appId
        ? '先选择 App'
        : platform === null
          ? '无法判断 App 平台（App 不在列表中或列表未读到），暂不能保存'
          : merchantInvalid
            ? '商户号须为 1–128 个字符'
            : mode === null
              ? '先选择 mode'
              : submitCredential && credentialMissing
                ? '凭据必填项未填完'
                : hostInvalid
                  ? '原生回跳 host 未填或不合规'
                  : null;

  const buildBody = (): UpsertPaymentProviderConfigRequest => {
    const body: UpsertPaymentProviderConfigRequest = {
      merchant_account_id: trimmedMerchant,
      mode: mode as PaymentProviderMode,
    };
    if (submitCredential) {
      const submitted: Record<string, string> = {};
      for (const field of capability.credential_fields) {
        const raw = credentialValue(field.name);
        // 标识类单行字段去首尾空白（服务端只规范化密钥材料，带空格会让连接测试被拒且无从回显排查）；
        // PEM 多行字段原样提交，由服务端去头尾与空白
        const value = presentation.multilineFields.includes(field.name) ? raw : raw.trim();
        // 可选字段留空不提交；必填字段已在 blockedReason 拦截
        if (value !== '' || field.required) submitted[field.name] = value;
      }
      body.credential = submitted;
    }
    if (native) body.native_return_base_url = nativeReturnUrlFor(normalizedHost);
    return body;
  };

  const save = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: () =>
      paymentProviderConfigApi.upsertConfig(channel, appId, buildBody(), idempotencyKey),
    onSuccess: () => {
      toast('已保存（未启用，需要测试连接）', { tone: 'pos' });
      onSaved();
    },
    onError: (error) => {
      if (error instanceof AdminApiError && error.code === 'VALIDATION_FAILED') {
        const next: FieldErrors = {};
        let unmatched = false;
        for (const item of error.errors ?? []) {
          const field = item.field;
          if (field === 'native_return_base_url') {
            if (detailsReasonIs(error.details, PAYMENT_PROVIDER_RETURN_HOST_NOT_REGISTERED)) {
              const hosts = Array.isArray(error.details?.allowed_hosts)
                ? (error.details.allowed_hosts as unknown[]).filter(
                    (h): h is string => typeof h === 'string',
                  )
                : [];
              setHostRejection(hosts);
              onAllowedHosts(appId, hosts);
            } else {
              next.host = native
                ? '形态不合规：只能是 https://<host>/payment/checkout/success'
                : 'Web App 不能填写原生回跳';
            }
          } else if (field === 'merchant_account_id') {
            next.merchant = '商户号须为 1–128 个字符';
          } else if (field === 'credential') {
            next.credential = submitCredential
              ? '凭据字段不全或多了字段，请按下方字段重新填写'
              : '沿用的凭据与商户号或 mode 不匹配：打开「更换凭据」重新提交整组凭据';
          } else if (
            presentation.merchantField !== null &&
            field === `credential.${presentation.merchantField}`
          ) {
            next.merchant = `商户号与凭据里的 ${presentation.merchantField} 不一致`;
          } else if (
            presentation.verificationField !== null &&
            field === `credential.${presentation.verificationField}`
          ) {
            const submitted = credentialValue(presentation.verificationField);
            if (!submitCredential) {
              next.credential =
                '沿用凭据里的平台公钥与该商户组已登记的验签材料不一致：到回调地址上「轮转验签材料」，或打开「更换凭据」重新提交';
            } else if (normalizeKeyMaterial(submitted) === '') {
              next[presentation.verificationField] = '密钥为空，或去掉 PEM 头尾后为空';
            } else {
              setMaterialMismatch(true);
            }
          } else if (field.startsWith('credential.')) {
            const name = field.slice('credential.'.length);
            next[name] = presentation.multilineFields.includes(name)
              ? '密钥为空，或去掉 PEM 头尾后为空'
              : '不能为空';
          } else {
            unmatched = true;
          }
        }
        setFieldErrors(next);
        setVersionConflict(false);
        if (unmatched) setNotice(describeProviderError(error, 'save', { channel }));
        return;
      }
      if (
        error instanceof AdminApiError &&
        error.code === 'RESOURCE_CONFLICT' &&
        detailsReasonIs(error.details, 'CONFIG_VERSION_CHANGED')
      ) {
        // 保留非密文字段，清空密文；新版本由列表重拉后经 existing 读到
        setCredential({});
        setIdempotencyKey(newIdempotencyKeyV7());
        setNotice(null);
        setMaterialMismatch(false);
        setVersionConflict(true);
        onRefresh();
        return;
      }
      setVersionConflict(false);
      const described = describeProviderError(error, 'save', {
        channel,
        deploymentMode,
        appName: (id) => apps.find((a) => a.id === id)?.name ?? id,
      });
      if (described.rotateIdempotencyKey) setIdempotencyKey(newIdempotencyKeyV7());
      if (described.refresh) onRefresh();
      if (described.placement === 'toast')
        toast(noticeToastText(described), { tone: noticeToastTone(described) });
      else setNotice(described);
    },
  });

  const pending = save.isPending;
  // 改组且本 App 是旧组唯一成员：旧组的回调地址只经配置列表派生，保存后不再在本页出现、也无法在本页停用或退役
  const orphanedEndpoints =
    groupChanged && existing
      ? configs.some(
          (c) =>
            c.app_id !== existing.app_id &&
            c.merchant_account_id === existing.merchant_account_id &&
            c.mode === existing.mode,
        )
        ? []
        : existing.webhook_endpoints
      : [];
  const close = () => {
    if (!pending) onClose();
  };
  const needsStopConfirm =
    isEdit && !!existing && existing.is_active && existing.connection_status === 'connected';
  const submit = () => {
    if (blockedReason || pending) return;
    if (needsStopConfirm) setConfirmOpen(true);
    else save.mutate();
  };

  const knownHosts = hostRejection ?? knownAllowedHosts[appId] ?? null;

  return (
    <Drawer
      icon={isEdit ? 'IconEdit' : 'IconPlus'}
      title={
        isEdit ? `编辑 ${copy.displayName} 配置 · ${appName}` : `新增 ${copy.displayName} 配置`
      }
      subtitle={
        isEdit
          ? '任何保存都会让该 App 回到待验证，需重新测试'
          : '为一个 App 录入商户凭据与原生回跳；保存后为待验证，不会启用'
      }
      width={720}
      onClose={close}
      closeOnEsc={!confirmOpen}
      footer={
        <>
          <div
            style={{
              flex: 1,
              fontSize: 11,
              color: needsStopConfirm || blockedReason ? 'var(--warn)' : 'var(--fg-muted)',
            }}
          >
            {blockedReason ??
              (needsStopConfirm
                ? `保存后 ${appName} 将停止下发 ${copy.displayName}，直到重新测试通过`
                : '保存后状态为「待验证」，需登记回调、写入验签材料并测试连接后才能启用')}
          </div>
          <button type="button" className="btn" disabled={pending} onClick={close}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={pending || blockedReason !== null}
            onClick={submit}
          >
            {pending ? '保存中…' : isEdit ? '保存…' : '保存（不启用）'}
          </button>
        </>
      }
    >
      <DialogBody gap={0}>
        {needsStopConfirm && (
          <div
            className="hstack"
            style={{
              gap: 10,
              alignItems: 'flex-start',
              padding: '10px 12px',
              background: 'var(--warn-soft)',
              borderRadius: 'var(--radius)',
              marginBottom: 4,
            }}
          >
            <Icons.IconAlert size={14} style={{ color: 'var(--warn)', marginTop: 2 }} />
            <div style={{ fontSize: 11.5, lineHeight: 1.55 }}>
              {appName} 当前<b>已启用且已连接</b>。保存后会回到「待验证」，{copy.displayName} 在{' '}
              {appName} 上<b>立即停止下发</b>，直到重新测试通过。保存前会再次确认。
            </div>
          </div>
        )}
        {notice && <ProviderNoticeBlock notice={notice} style={{ marginBottom: 4 }} />}
        {versionConflict && (
          <ProviderNoticeBlock
            style={{ marginBottom: 4 }}
            notice={{
              kind: 'generic',
              tone: 'neg',
              title: '配置已被他人修改',
              body: `已载入最新配置${existing ? `（v${existing.config_version}）` : ''}，保留你填写的商户号、mode 与回跳 host；凭据输入已清空，请重新粘贴后保存。`,
              code: 'RESOURCE_CONFLICT · CONFIG_VERSION_CHANGED',
              placement: 'inline',
            }}
          />
        )}

        <FormRow
          label="App"
          required={!isEdit}
          hint={isEdit ? undefined : '只列出平台适用且未配置的 App'}
          labelWidth={LABEL_WIDTH}
        >
          {isEdit ? (
            <div className="hstack" style={{ gap: 8 }}>
              <AppIcon app={app} label={app ? undefined : '?'} size="sm" />
              <span style={{ fontSize: 12.5 }}>{appName}</span>
              <span className="chip mono" style={{ fontSize: 9.5 }}>
                {platform ?? '平台未知'}
              </span>
              {existing && (
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                  config_version {existing.config_version}
                </span>
              )}
            </div>
          ) : candidates.length === 0 ? (
            <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>没有可新增的 App</span>
          ) : (
            <Select
              value={appId}
              disabled={pending}
              style={{ maxWidth: 320 }}
              onChange={(e) => {
                setAppId(e.target.value);
                setHostRejection(null);
                touch('host');
              }}
            >
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {`${candidate.name} · ${providerPlatformOf(candidate) ?? ''}`}
                </option>
              ))}
            </Select>
          )}
        </FormRow>

        <FormRow
          label="商户号"
          required
          hint={isEdit ? '改商户号或 mode 等于换组，必须重新提交凭据' : copy.merchantAccountHint}
          labelWidth={LABEL_WIDTH}
        >
          <input
            className="input mono"
            value={merchant}
            maxLength={MERCHANT_MAX}
            disabled={pending}
            aria-label="商户号"
            aria-invalid={fieldErrors.merchant ? true : undefined}
            onChange={(e) => {
              setMerchant(e.target.value);
              touch('merchant');
            }}
            style={{ maxWidth: 320 }}
          />
          {fieldErrors.merchant && <FieldError text={fieldErrors.merchant} />}
          {groupChanged && existing ? (
            <div
              style={{
                marginTop: 8,
                padding: '8px 10px',
                background: 'var(--warn-soft)',
                borderRadius: 'var(--radius)',
                fontSize: 11.5,
                lineHeight: 1.55,
              }}
            >
              将离开组{' '}
              <span className="mono">
                {existing.merchant_account_id} · {existing.mode}
              </span>
              ，加入组{' '}
              <span className="mono">
                {trimmedMerchant || '—'} · {mode ?? '—'}
              </span>
              。沿用的凭据会被拒，必须重新提交整组凭据。
              {orphanedEndpoints.length > 0 && (
                <>
                  <br />
                  {appName} 是旧组唯一的配置：保存后旧组的 {orphanedEndpoints.length}{' '}
                  个回调地址不再在本页显示，也无法在本页停用。旧组仍有订单需要长期读取（退款、争议、对账）时，这些回调地址与验签材料要保留、不要退役；需要停用的话，先停用再改组。
                </>
              )}
            </div>
          ) : !isEdit && targetGroupKey ? (
            <div
              className="card-flat"
              style={{
                padding: '8px 10px',
                marginTop: 8,
                fontSize: 11.5,
                color: 'var(--fg-muted)',
                lineHeight: 1.55,
              }}
            >
              {targetGroupMembers.length > 0 ? (
                <>
                  将加入商户账户组{' '}
                  <span className="mono" style={{ color: 'var(--fg)' }}>
                    {trimmedMerchant} · {mode}
                  </span>
                  ：已有{' '}
                  {targetGroupMembers
                    .map((c) => apps.find((a) => a.id === c.app_id)?.name ?? c.app_id)
                    .join('、')}
                  ，{targetGroupEndpoints.filter((e) => e.is_active).length} 个启用中的回调地址，
                  {targetGroupHasMaterial ? `${copy.materialName}已登记。` : '尚未写入验签材料。'}
                </>
              ) : (
                <>
                  将新建商户账户组{' '}
                  <span className="mono" style={{ color: 'var(--fg)' }}>
                    {trimmedMerchant} · {mode}
                  </span>
                  ，保存后再为它登记回调地址。
                </>
              )}
            </div>
          ) : null}
        </FormRow>

        <FormRow label="mode" required hint="须与本环境一致" labelWidth={LABEL_WIDTH}>
          <div className="hstack" style={{ gap: 10 }}>
            <div className="seg" role="radiogroup" aria-label="mode">
              {(['TEST', 'LIVE'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={mode === m}
                  className={mode === m ? 'on' : undefined}
                  disabled={pending || deploymentMode === null}
                  onClick={() => {
                    setMode(m);
                    touch('mode');
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
            <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
              {deploymentMode ? (
                <>
                  本环境 <span style={{ color: 'var(--fg)' }}>{deploymentMode}</span>
                </>
              ) : (
                'deployment_mode = null'
              )}
            </span>
          </div>
          {deploymentMode === null ? (
            <div
              style={{
                marginTop: 8,
                padding: '8px 10px',
                background: 'var(--warn-soft)',
                borderRadius: 'var(--radius)',
                fontSize: 11.5,
                lineHeight: 1.55,
              }}
            >
              环境级问题：保存必然被拒（MODE_MISMATCH）。在页面上重填没有用，请联系运维。
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 6, lineHeight: 1.5 }}>
              {mode && mode !== deploymentMode
                ? `所选 mode 与本环境（${deploymentMode}）不一致，保存会被拒。`
                : `${copy.displayName} 凭据本身不带环境信号，凭据是否属于本环境要靠「测试连接」确认。`}
            </div>
          )}
        </FormRow>

        {isEdit && (
          <FormRow label="凭据" hint="只写 · 不回显" labelWidth={LABEL_WIDTH}>
            <div className="hstack" style={{ gap: 10 }}>
              <Switch
                checked={submitCredential}
                locked={groupChanged}
                disabled={pending}
                ariaLabel="更换凭据"
                onChange={(next) => {
                  setReplaceCredential(next);
                  if (!next) setCredential({});
                  touch('credential');
                }}
              />
              <span style={{ fontSize: 12.5 }}>更换凭据{groupChanged ? '（已锁定开启）' : ''}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 6, lineHeight: 1.5 }}>
              关闭 =
              沿用当前凭据，凭据组不提交。系统不保存可回显的凭据，这里不会显示掩码、长度或尾号。
            </div>
            {fieldErrors.credential && <FieldError text={fieldErrors.credential} />}
          </FormRow>
        )}

        {submitCredential && (
          <>
            <div style={{ padding: '14px 0 6px' }} className="hstack">
              <Icons.IconLock size={13} />
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>凭据</span>
              <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                字段来自能力声明 · 只写，任何时候都不回显
              </span>
            </div>
            {!isEdit && fieldErrors.credential && <FieldError text={fieldErrors.credential} />}
            {capability.credential_fields.map((field) => {
              const synced = field.name === presentation.merchantField;
              const multiline = presentation.multilineFields.includes(field.name);
              const isVerification = field.name === presentation.verificationField;
              const error = fieldErrors[field.name];
              const inputId = `provider-credential-${field.name}`;
              const value = credentialValue(field.name);
              const onValue = (next: string) => {
                setCredential((prev) => ({ ...prev, [field.name]: next }));
                touch(field.name);
              };
              return (
                <FormRow
                  key={field.name}
                  label={
                    <label htmlFor={inputId} className="mono" style={{ fontSize: 12 }}>
                      {field.name}
                    </label>
                  }
                  required={field.required}
                  hint={field.secret ? '密钥 · 遮蔽输入' : undefined}
                  labelWidth={LABEL_WIDTH}
                  pad="10px 0"
                >
                  {synced ? (
                    <div className="hstack">
                      <input
                        id={inputId}
                        className="input mono"
                        value={value}
                        readOnly
                        style={{
                          maxWidth: 320,
                          background: 'var(--bg-subtle)',
                          color: 'var(--fg-muted)',
                        }}
                      />
                      <span className="chip" style={{ fontSize: 10 }}>
                        同步自商户号
                      </span>
                    </div>
                  ) : field.secret ? (
                    multiline ? (
                      <PasswordInput
                        multiline
                        id={inputId}
                        rows={3}
                        value={value}
                        disabled={pending}
                        aria-invalid={error ? true : undefined}
                        onChange={(e) => onValue(e.target.value)}
                      />
                    ) : (
                      <PasswordInput
                        id={inputId}
                        className="input mono"
                        autoComplete="off"
                        value={value}
                        disabled={pending}
                        aria-invalid={error ? true : undefined}
                        onChange={(e) => onValue(e.target.value)}
                        style={{ maxWidth: 320 }}
                      />
                    )
                  ) : multiline ? (
                    <textarea
                      id={inputId}
                      className="input mono"
                      rows={3}
                      spellCheck={false}
                      autoComplete="off"
                      value={value}
                      disabled={pending}
                      aria-invalid={error ? true : undefined}
                      onChange={(e) => onValue(e.target.value)}
                      style={{ fontSize: 11, width: '100%', boxSizing: 'border-box' }}
                    />
                  ) : (
                    <input
                      id={inputId}
                      className="input mono"
                      autoComplete="off"
                      spellCheck={false}
                      value={value}
                      disabled={pending}
                      aria-invalid={error ? true : undefined}
                      onChange={(e) => onValue(e.target.value)}
                      style={{ maxWidth: 320 }}
                    />
                  )}
                  {multiline && (
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--fg-muted)',
                        marginTop: 4,
                        lineHeight: 1.5,
                      }}
                    >
                      粘贴 PEM，带或不带头尾均可
                      {isVerification && (
                        <>
                          。须与该商户组已登记的验签材料相同；更换平台公钥只能到回调地址上「轮转验签材料」。
                          {targetGroupKey && targetGroupHasMaterial && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              style={{
                                padding: '0 4px',
                                height: 'auto',
                                color: 'var(--accent-fg)',
                              }}
                              disabled={pending}
                              onClick={() => onOpenRotate(targetGroupKey)}
                            >
                              去轮转
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {error && <FieldError text={error} />}
                  {isVerification && materialMismatch && (
                    <div
                      role="alert"
                      style={{
                        marginTop: 8,
                        padding: '8px 10px',
                        background: 'var(--neg-soft)',
                        borderRadius: 'var(--radius)',
                        fontSize: 11.5,
                        lineHeight: 1.55,
                      }}
                    >
                      <span style={{ color: 'var(--neg)', fontWeight: 500 }}>
                        这个商户号已经登记了另一把平台公钥。
                      </span>
                      如需更换，到回调地址上「轮转验签材料」。
                      <div
                        className="mono"
                        style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 2 }}
                      >
                        VALIDATION_FAILED · credential.{presentation.verificationField}
                      </div>
                    </div>
                  )}
                </FormRow>
              );
            })}
          </>
        )}

        <FormRow
          label="原生回跳"
          required={native}
          hint={native ? '原生 App 必填 · 只填 host，路径固定' : undefined}
          labelWidth={LABEL_WIDTH}
        >
          {platform === 'WEB' ? (
            <div className="hstack" style={{ gap: 8 }}>
              <span className="chip" style={{ fontSize: 10 }}>
                不适用
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                Web App 不填写，提交时不带该字段
              </span>
            </div>
          ) : (
            <>
              <HostOnlyUrlInput
                id="provider-native-return-host"
                host={host}
                path={NATIVE_RETURN_PATH}
                disabled={pending}
                invalid={!!fieldErrors.host || hostRejection !== null}
                describedBy={
                  hostRejection !== null
                    ? 'provider-native-return-host-err'
                    : fieldErrors.host
                      ? 'provider-native-return-host-field-err'
                      : undefined
                }
                onChange={(next) => {
                  setHost(next);
                  setHostRejection(null);
                  touch('host');
                }}
              />
              {fieldErrors.host && (
                <FieldError id="provider-native-return-host-field-err" text={fieldErrors.host} />
              )}
              {hostRejection !== null ? (
                <div
                  id="provider-native-return-host-err"
                  role="alert"
                  style={{
                    marginTop: 8,
                    padding: '10px 12px',
                    background: 'var(--neg-soft)',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--neg)' }}>
                    {hostRejection.length === 0
                      ? `本环境还没有为 ${appName} 登记任何回跳 host`
                      : `这个 host 没有在本环境为 ${appName} 登记`}
                  </div>
                  <div
                    className="mono"
                    style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 2 }}
                  >
                    VALIDATION_FAILED · native_return_base_url · RETURN_HOST_NOT_REGISTERED
                  </div>
                  {hostRejection.length > 0 && (
                    <>
                      <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 8 }}>
                        本环境为该 App 登记的 host：
                      </div>
                      <div className="hstack" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        {hostRejection.map((allowed) => (
                          <button
                            key={allowed}
                            type="button"
                            className="btn btn-sm"
                            disabled={pending}
                            onClick={() => {
                              setHost(allowed);
                              setHostRejection(null);
                              touch('host');
                            }}
                          >
                            <span className="mono">{allowed}</span>
                            <span style={{ color: 'var(--fg-muted)' }}>使用</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 8 }}>
                    {hostRejection.length === 0
                      ? '在这里改写无法通过校验，请联系运维登记后再保存。'
                      : '需要新的 host 请联系运维登记，在这里改写无法通过校验。'}
                  </div>
                </div>
              ) : knownHosts && knownHosts.length > 0 ? (
                <div
                  className="hstack"
                  style={{
                    gap: 6,
                    marginTop: 6,
                    fontSize: 11,
                    color: 'var(--fg-muted)',
                    flexWrap: 'wrap',
                  }}
                >
                  本环境已登记：
                  {knownHosts.map((h) => (
                    <span key={h} className="chip mono" style={{ fontSize: 10 }}>
                      {h}
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </FormRow>
      </DialogBody>

      {confirmOpen && (
        <ConfirmDialog
          tone="warn"
          title={`保存后 ${appName} 将停止下发 ${copy.displayName}`}
          message={`${appName} 当前已启用且已连接。保存会让它回到「待验证」，在重新测试通过之前，C 端不会为 ${appName} 下发 ${copy.displayName} 支付方式。`}
          details={['进行中的订单与回调不受影响', '保存后在本行点「测试连接」即可恢复']}
          confirmLabel="保存并停止下发"
          onConfirm={() => save.mutate()}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </Drawer>
  );
}

function FieldError({ text, id }: { text: string; id?: string }) {
  return (
    <div
      id={id}
      role="alert"
      style={{ marginTop: 6, fontSize: 11.5, color: 'var(--neg)', lineHeight: 1.5 }}
    >
      {text}
    </div>
  );
}
